import { Auth0Client } from "@auth0/auth0-spa-js";
import { QueryClient } from "@tanstack/react-query";

import { Auth0AuthenticationClient } from "@/lib/auth/auth0-authentication-client";
import { AuthenticationClientDispatch } from "@/lib/auth/authentication-client-dispatch";
import { StytchAuthenticationClient } from "@/lib/auth/stytch-authentication-client";
import { createInitialUser } from "@/lib/create-initial-user";
import { createWsClient } from "@/lib/graphql-client";
import type { UserContext } from "@/lib/interfaces/user-context";
import type { WebSocketClient } from "@/lib/interfaces/websocket-client";
import type { FeatureFlags, LaunchDarklyClient } from "@/lib/launchdarkly";
import { preferencesStorageCache } from "@/lib/storage/preferences-storage-cache";
import { stytchClient } from "@/lib/stytch";
import { audience, auth0Domain, callbackUri, clientId, scope } from "@/lib/utils/config";
import { WebsocketClientDispatch } from "@/lib/websocket-client-dispatch";

export interface RootRouteContext {
  auth0AuthenticationClient: Auth0AuthenticationClient;
  stytchAuthenticationClient: StytchAuthenticationClient;
  authClient: AuthenticationClientDispatch;
  queryClient: QueryClient;
  ldClient: LaunchDarklyClient;
  wsClient: WebSocketClient;
  user: UserContext;
  flags: FeatureFlags;
}

const queryClient = new QueryClient();

const auth0Client = new Auth0Client({
  domain: auth0Domain,
  clientId,
  useRefreshTokens: true,
  useRefreshTokensFallback: false,
  cache: preferencesStorageCache,
  authorizationParams: {
    redirect_uri: callbackUri,
    audience,
    scope,
  },
});

const auth0AuthenticationClient = new Auth0AuthenticationClient(auth0Client);
const stytchAuthenticationClient = new StytchAuthenticationClient(stytchClient);
void stytchAuthenticationClient.initialize();

const authClient = new AuthenticationClientDispatch(
  auth0AuthenticationClient,
  stytchAuthenticationClient,
);

const auth0WebSocketClient = createWsClient(auth0AuthenticationClient);
const stytchWebSocketClient = createWsClient(stytchAuthenticationClient);

const wsClient = new WebsocketClientDispatch(
  auth0WebSocketClient,
  stytchWebSocketClient,
  authClient,
);

const user = createInitialUser();

export const initialRootContext = {
  auth0AuthenticationClient,
  stytchAuthenticationClient,
  authClient,
  wsClient,
  queryClient,
  user,
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- Will be defined at root level
  // biome-ignore lint/style/noNonNullAssertion: legacy code
  ldClient: undefined!,
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- Will be defined at root level
  // biome-ignore lint/style/noNonNullAssertion: legacy code
  flags: undefined!,
} satisfies RootRouteContext;
